import type { ThemePreference } from '@easiibill/types';
import { create } from 'zustand';
import { api } from './api';

const STORAGE_KEY = 'eb.theme';
const media = typeof window !== 'undefined' ? window.matchMedia('(prefers-color-scheme: dark)') : null;

function stored(): ThemePreference | null {
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    return value === 'light' || value === 'dark' || value === 'system' ? value : null;
  } catch {
    return null;
  }
}

function apply(theme: ThemePreference): boolean {
  const dark = theme === 'dark' || (theme === 'system' && Boolean(media?.matches));
  document.documentElement.classList.toggle('dark', dark);
  document.querySelector('meta[name="theme-color"]')?.setAttribute('content', dark ? '#0a0d14' : '#f5f7fb');
  return dark;
}

interface ThemeState {
  theme: ThemePreference;
  dark: boolean;
  setTheme: (theme: ThemePreference) => void;
}

export const useTheme = create<ThemeState>((set, get) => {
  const initial = stored() ?? 'system';
  media?.addEventListener('change', () => {
    if (get().theme === 'system') set({ dark: apply('system') });
  });
  return {
    theme: initial,
    dark: apply(initial),
    setTheme: (theme) => {
      try {
        localStorage.setItem(STORAGE_KEY, theme);
      } catch {
        // Not persisted when storage is unavailable.
      }
      set({ theme, dark: apply(theme) });
    },
  };
});

/** Applies a theme chosen by the user and saves it to their account for other devices. */
export function chooseTheme(theme: ThemePreference): Promise<void> {
  useTheme.getState().setTheme(theme);
  return api('/api/account/preferences', { method: 'PATCH', body: { theme } }).then(
    () => undefined,
    () => undefined,
  );
}

/** Uses the account's saved theme on a device that has no local choice yet. */
export function adoptAccountTheme(theme: ThemePreference): void {
  if (stored() === null && theme !== useTheme.getState().theme) useTheme.getState().setTheme(theme);
}
